import { useState, useEffect, useCallback } from 'react';
import { Calendar, Clock, Users, ChevronLeft, ChevronRight, X, Trash2, Settings, UserPlus, AlertTriangle, RefreshCw, BarChart3, TrendingUp, UserMinus, Info } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip as RechartsTooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { PageHeader } from '../components/common/PageHeader';
import { DataCard } from '../components/common/DataCard';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { useApp } from '../contexts/AppContext';
import api from '../services/api';
import { toast } from 'sonner';

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

export default function TeacherSchedulePage() {
  const { currentUser, isAdmin } = useApp();
  const isTeacher = currentUser?.role === 'teacher';

  const [view, setView] = useState('week');
  const [config, setConfig] = useState(null);
  const [timetable, setTimetable] = useState([]);
  const [schedule, setSchedule] = useState([]);
  const [substitutes, setSubstitutes] = useState([]);
  const [selectedClass, setSelectedClass] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());

  // Edit period
  const [editingCell, setEditingCell] = useState(null);
  const [editForm, setEditForm] = useState({ subject: '', teacherId: '', startTime: '', endTime: '' });
  const [isSaving, setIsSaving] = useState(false);

  // Substitute dialog
  const [subDialog, setSubDialog] = useState(null); // {day, period, date, entry}
  const [availableSubs, setAvailableSubs] = useState([]);
  const [loadingSubs, setLoadingSubs] = useState(false);
  const [assigningSub, setAssigningSub] = useState(false);

  // Config dialog
  const [showConfig, setShowConfig] = useState(false);
  const [configForm, setConfigForm] = useState({ periodsPerDay: 8, periodDuration: 45, startTime: '08:00' });

  // Report data
  const [report, setReport] = useState(null);
  const [loadingReport, setLoadingReport] = useState(false);

  // Month leave data
  const [monthLeaves, setMonthLeaves] = useState([]);

  useEffect(() => { loadConfig(); }, []);
  useEffect(() => { if (config) loadTimetable(); }, [selectedClass, config]);
  useEffect(() => { loadScheduleData(); }, []);
  useEffect(() => { if (view === 'report' && isAdmin && !report) loadReport(); }, [view]);
  useEffect(() => { if (isAdmin) loadMonthLeaves(); }, [currentDate]);

  const loadConfig = async () => {
    try {
      const data = await api.getTimetableConfig();
      setConfig(data);
      setConfigForm({ periodsPerDay: data.periodsPerDay || 8, periodDuration: data.periodDuration || 45, startTime: data.startTime || '08:00' });
      if (data.classSections?.length > 0 && !selectedClass) setSelectedClass(data.classSections[0]);
    } catch (e) { console.error(e); } finally { setIsLoading(false); }
  };

  const loadTimetable = async () => {
    try {
      const params = {};
      if (isTeacher) params.teacherId = currentUser.userId;
      else if (selectedClass) params.classSection = selectedClass;
      const data = await api.getTimetable(params);
      setTimetable(data);
    } catch (e) { console.error(e); }
  };

  const loadScheduleData = async () => {
    try {
      const data = await api.getTeacherSchedule('week');
      setSchedule(data.schedule || []);
      setSubstitutes(data.substitutes || []);
    } catch (e) { console.error(e); }
  };

  const loadReport = async () => {
    setLoadingReport(true);
    try {
      const data = await api.getSubstituteReport();
      setReport(data);
    } catch (e) { toast.error('Failed to load report'); }
    finally { setLoadingReport(false); }
  };

  const loadMonthLeaves = async () => {
    try {
      const leaves = await api.getLeaves('approved');
      setMonthLeaves(leaves || []);
    } catch (e) { console.error(e); }
  };

  const getLeavesForDate = useCallback((dateStr) => {
    return monthLeaves.filter(l => l.startDate <= dateStr && l.endDate >= dateStr);
  }, [monthLeaves]);

  const getEntry = useCallback((day, period) => {
    return timetable.find(t => t.dayOfWeek === day && t.periodNumber === period);
  }, [timetable]);

  const getSubstitute = useCallback((day, period, date) => {
    return substitutes.find(s => s.dayOfWeek === day && s.periodNumber === period && s.classSection === selectedClass && s.date === date);
  }, [substitutes, selectedClass]);

  // Auto-calculate period time from config
  const calcPeriodTime = useCallback((periodNum) => {
    if (!config?.startTime || !config?.periodDuration) return { start: '', end: '' };
    const [h, m] = config.startTime.split(':').map(Number);
    const baseMin = h * 60 + m;
    const startMin = baseMin + (periodNum - 1) * config.periodDuration;
    const endMin = startMin + config.periodDuration;
    const fmt = (mins) => `${String(Math.floor(mins / 60) % 24).padStart(2, '0')}:${String(mins % 60).padStart(2, '0')}`;
    return { start: fmt(startMin), end: fmt(endMin) };
  }, [config]);

  // Edit timetable
  const startEdit = (day, period) => {
    if (!isAdmin) return;
    const existing = getEntry(day, period);
    const defaultTime = calcPeriodTime(period);
    setEditForm({
      subject: existing?.subject || '',
      teacherId: existing?.teacherId || '',
      startTime: existing?.startTime || defaultTime.start,
      endTime: existing?.endTime || defaultTime.end,
    });
    setEditingCell({ day, period });
  };

  const saveEntry = async () => {
    if (!editForm.subject) { toast.error('Subject is required'); return; }
    setIsSaving(true);
    try {
      const teacher = config?.teachers?.find(t => t.teacherId === editForm.teacherId);
      await api.saveTimetable([{
        classSection: selectedClass, dayOfWeek: editingCell.day, periodNumber: editingCell.period,
        subject: editForm.subject, teacherId: editForm.teacherId || null,
        teacherName: teacher?.name || null, startTime: editForm.startTime || null, endTime: editForm.endTime || null,
      }]);
      toast.success('Period saved');
      setEditingCell(null);
      loadTimetable();
    } catch (e) { toast.error(e.message || 'Failed to save'); }
    finally { setIsSaving(false); }
  };

  const deleteEntry = async (day, period) => {
    try {
      await api.deleteTimetableEntry(selectedClass, day, period);
      toast.success('Period removed');
      loadTimetable();
    } catch (e) { toast.error(e.message || 'Failed to delete'); }
  };

  // Substitute flow
  const openSubDialog = async (day, period, entry) => {
    const weekDays = getWeekDays();
    const dayIdx = DAYS.indexOf(day);
    const dateForDay = dayIdx >= 0 && dayIdx < weekDays.length ? weekDays[dayIdx].toISOString().split('T')[0] : new Date().toISOString().split('T')[0];
    setSubDialog({ day, period, date: dateForDay, entry });
    setLoadingSubs(true);
    try {
      const available = await api.getAvailableSubstitutes(dateForDay, period, selectedClass);
      setAvailableSubs(available);
    } catch (e) { toast.error('Failed to load available teachers'); setAvailableSubs([]); }
    finally { setLoadingSubs(false); }
  };

  const assignSub = async (subTeacherId) => {
    setAssigningSub(true);
    try {
      await api.assignSubstitute({
        date: subDialog.date, classSection: selectedClass, periodNumber: subDialog.period,
        originalTeacherId: subDialog.entry.teacherId, substituteTeacherId: subTeacherId,
        reason: 'Teacher on leave',
      });
      toast.success('Substitute assigned');
      setSubDialog(null);
      loadScheduleData();
    } catch (e) { toast.error(e.message || 'Failed to assign'); }
    finally { setAssigningSub(false); }
  };

  const removeSub = async (subId) => {
    try {
      await api.removeSubstitute(subId);
      toast.success('Substitute removed');
      loadScheduleData();
    } catch (e) { toast.error(e.message || 'Failed to remove'); }
  };

  const saveConfig = async () => {
    try {
      await api.updateTimetableConfig(configForm);
      toast.success('Configuration updated');
      setShowConfig(false);
      loadConfig();
    } catch (e) { toast.error('Failed to update config'); }
  };

  const periodsCount = config?.periodsPerDay || 8;
  const periods = Array.from({ length: periodsCount }, (_, i) => i + 1);
  const onLeaveTeachers = schedule.filter(t => t.onLeave);
  const onLeaveIds = new Set(onLeaveTeachers.map(t => t.teacherId));

  const getWeekDays = () => {
    const d = new Date(currentDate);
    const day = d.getDay();
    const monday = new Date(d);
    monday.setDate(d.getDate() - (day === 0 ? 6 : day - 1));
    return Array.from({ length: 6 }, (_, i) => { const dd = new Date(monday); dd.setDate(monday.getDate() + i); return dd; });
  };

  const getMonthDays = () => {
    const year = currentDate.getFullYear(), month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const offset = firstDay === 0 ? 6 : firstDay - 1;
    const days = [];
    for (let i = 0; i < offset; i++) days.push(null);
    for (let i = 1; i <= daysInMonth; i++) days.push(i);
    return days;
  };

  const weekDays = getWeekDays();
  const today = new Date().toISOString().split('T')[0];
  const todayDayName = new Date().toLocaleDateString('en-US', { weekday: 'long' });

  if (isLoading) return <div className="flex items-center justify-center py-20"><div className="w-10 h-10 border-4 border-brand-light-blue border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="teacher-schedule-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <PageHeader title="Teacher Schedule" description={isTeacher ? 'Your class schedule (view only).' : 'Manage periods, assign substitutes.'} />
        <div className="flex items-center gap-2">
          {isAdmin && (
            <>
              <Select value={selectedClass} onValueChange={setSelectedClass}>
                <SelectTrigger className="w-[140px]" data-testid="class-select"><SelectValue placeholder="Select Class" /></SelectTrigger>
                <SelectContent>{config?.classSections?.map(cs => <SelectItem key={cs} value={cs}>{cs}</SelectItem>)}</SelectContent>
              </Select>
              <Button variant="outline" size="sm" onClick={() => setShowConfig(true)} data-testid="config-btn"><Settings className="w-4 h-4 mr-1" /> Config</Button>
            </>
          )}
          <Select value={view} onValueChange={setView}>
            <SelectTrigger className="w-[120px]" data-testid="view-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="day">Daily</SelectItem>
              <SelectItem value="week">Weekly</SelectItem>
              <SelectItem value="month">Monthly</SelectItem>
              {isAdmin && <SelectItem value="report">Report</SelectItem>}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Summary — Admin only */}
      {isAdmin && (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <DataCard className="text-center">
          <div className="text-2xl font-bold text-brand-deep-blue">{schedule.length}</div>
          <p className="text-xs text-muted-foreground mt-1">Total Teachers</p>
        </DataCard>
        <DataCard className="text-center">
          <div className="text-2xl font-bold text-emerald-600">{schedule.length - onLeaveTeachers.length}</div>
          <p className="text-xs text-muted-foreground mt-1">Available</p>
        </DataCard>
        <DataCard className="text-center">
          <div className="text-2xl font-bold text-amber-500">{onLeaveTeachers.length}</div>
          <p className="text-xs text-muted-foreground mt-1">On Leave</p>
        </DataCard>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <DataCard className="text-center cursor-pointer hover:border-purple-300 transition-colors" onClick={() => { setView('report'); }} data-testid="substitutes-assigned-card">
                <div className="text-2xl font-bold text-purple-600">{substitutes.length}</div>
                <p className="text-xs text-muted-foreground mt-1">Substitutes Assigned</p>
              </DataCard>
            </TooltipTrigger>
            <TooltipContent><p>Click to view substitute teacher assignment report</p></TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
      )}

      {/* On Leave Banner */}
      {onLeaveTeachers.length > 0 && isAdmin && (
        <div className="p-3 bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800 rounded-lg">
          <div className="flex items-center gap-2 mb-2"><AlertTriangle className="w-4 h-4 text-amber-600" /><span className="text-sm font-semibold text-amber-700">Teachers on Leave</span></div>
          <div className="flex flex-wrap gap-2">
            {onLeaveTeachers.map(t => (
              <Badge key={t.teacherId} className="bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400">
                {t.name} — {t.leaveType} ({t.leaveStart} to {t.leaveEnd})
              </Badge>
            ))}
          </div>
        </div>
      )}

      {/* Weekly View */}
      {view === 'week' && (
        <DataCard className="overflow-x-auto">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold flex items-center gap-2"><Calendar className="w-4 h-4" /> {isTeacher ? 'My Weekly Schedule' : `Weekly Timetable — ${selectedClass}`}</h3>
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="icon" onClick={() => { const d = new Date(currentDate); d.setDate(d.getDate() - 7); setCurrentDate(d); }}><ChevronLeft className="w-4 h-4" /></Button>
              <span className="text-sm font-medium">{weekDays[0]?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - {weekDays[5]?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
              <Button variant="ghost" size="icon" onClick={() => { const d = new Date(currentDate); d.setDate(d.getDate() + 7); setCurrentDate(d); }}><ChevronRight className="w-4 h-4" /></Button>
            </div>
          </div>
          <div className="min-w-[700px]">
            <table className="w-full border-collapse">
              <thead>
                <tr>
                  <th className="p-2 text-xs font-semibold text-muted-foreground text-left border-b dark:border-slate-700 w-16">Period</th>
                  {DAYS.map((day, di) => {
                    const dayDate = weekDays[di]?.toISOString().split('T')[0];
                    const dayLeaves = dayDate ? getLeavesForDate(dayDate) : [];
                    return (
                    <th key={day} className={`p-2 text-xs font-semibold text-center border-b dark:border-slate-700 ${day === todayDayName ? 'bg-brand-deep-blue/5 text-brand-deep-blue' : 'text-muted-foreground'}`}>
                      <div>{day.substring(0, 3)}</div>
                      <div className="text-[10px] font-normal">{weekDays[di]?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</div>
                      {isAdmin && dayLeaves.length > 0 && (
                        <TooltipProvider delayDuration={200}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div className="text-[9px] text-red-500 font-normal mt-0.5 cursor-help" data-testid={`leave-tooltip-${day}`}>
                                <AlertTriangle className="w-2.5 h-2.5 inline -mt-px mr-0.5" />{dayLeaves.length} on leave
                              </div>
                            </TooltipTrigger>
                            <TooltipContent side="bottom" className="max-w-[200px]">
                              <p className="font-semibold text-xs mb-1">On Leave:</p>
                              {dayLeaves.map((l, li) => (
                                <p key={li} className="text-xs">{l.teacherName} <span className="text-muted-foreground">({l.leaveType || 'Leave'})</span></p>
                              ))}
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      )}
                    </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {periods.map(p => (
                  <tr key={p}>
                    <td className="p-2 text-xs font-medium text-muted-foreground border-b dark:border-slate-700">P{p}</td>
                    {DAYS.map((day, di) => {
                      const entry = getEntry(day, p);
                      const dateForDay = weekDays[di]?.toISOString().split('T')[0];
                      const sub = dateForDay ? getSubstitute(day, p, dateForDay) : null;
                      const isEditing = editingCell?.day === day && editingCell?.period === p;
                      const dayLeaves = dateForDay ? getLeavesForDate(dateForDay) : [];
                      const dayLeaveTeacherIds = new Set(dayLeaves.map(l => l.teacherId));
                      const teacherOnLeave = entry?.teacherId && (onLeaveIds.has(entry.teacherId) || dayLeaveTeacherIds.has(entry.teacherId));

                      return (
                        <td key={day} className={`p-1 border-b dark:border-slate-700 ${day === todayDayName ? 'bg-brand-deep-blue/5' : ''}`}>
                          {isEditing ? (
                            <div className="p-2 space-y-1.5 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                              <Input value={editForm.subject} onChange={e => setEditForm({...editForm, subject: e.target.value})} placeholder="Subject" className="h-7 text-xs" data-testid="edit-subject" />
                              <Select value={editForm.teacherId} onValueChange={v => setEditForm({...editForm, teacherId: v})}>
                                <SelectTrigger className="h-7 text-xs" data-testid="edit-teacher"><SelectValue placeholder="Assign Teacher" /></SelectTrigger>
                                <SelectContent>{config?.teachers?.map(t => <SelectItem key={t.teacherId} value={t.teacherId}>{t.name}</SelectItem>)}</SelectContent>
                              </Select>
                              <div className="grid grid-cols-2 gap-1.5">
                                <div>
                                  <label className="text-[10px] font-medium text-muted-foreground mb-0.5 block">Start</label>
                                  <Input type="time" value={editForm.startTime} onChange={e => setEditForm({...editForm, startTime: e.target.value})} className="h-7 text-xs" data-testid="edit-start-time" />
                                </div>
                                <div>
                                  <label className="text-[10px] font-medium text-muted-foreground mb-0.5 block">End</label>
                                  <Input type="time" value={editForm.endTime} onChange={e => setEditForm({...editForm, endTime: e.target.value})} className="h-7 text-xs" data-testid="edit-end-time" />
                                </div>
                              </div>
                              <div className="flex gap-1 pt-0.5">
                                <Button size="sm" className="h-7 text-xs flex-1 bg-brand-deep-blue" onClick={saveEntry} disabled={isSaving} data-testid="save-period-btn">{isSaving ? '...' : 'Save'}</Button>
                                <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setEditingCell(null)}><X className="w-3 h-3" /></Button>
                              </div>
                            </div>
                          ) : entry ? (
                            <div className={`p-1.5 rounded text-center relative group ${teacherOnLeave && !sub ? 'bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-800' : sub ? 'bg-purple-50 dark:bg-purple-900/10 border border-purple-200 dark:border-purple-800' : isAdmin ? 'hover:bg-slate-100 dark:hover:bg-slate-800 cursor-pointer' : ''}`} onClick={() => !teacherOnLeave && isAdmin && startEdit(day, p)} data-testid={`cell-${day}-${p}`}>
                              <p className="text-xs font-semibold text-foreground">{entry.subject}</p>
                              {sub ? (
                                <div>
                                  <p className="text-[10px] text-purple-600 font-medium flex items-center justify-center gap-0.5"><RefreshCw className="w-2.5 h-2.5" />{sub.substituteTeacherName}</p>
                                  <p className="text-[10px] text-muted-foreground line-through">{entry.teacherName}</p>
                                  {isAdmin && <button onClick={(e) => { e.stopPropagation(); removeSub(sub.substituteId); }} className="opacity-0 group-hover:opacity-100 absolute top-0 right-0 p-0.5"><Trash2 className="w-3 h-3 text-red-400" /></button>}
                                </div>
                              ) : teacherOnLeave ? (
                                <div>
                                  <p className="text-[10px] text-red-500 line-through">{entry.teacherName}</p>
                                  {isAdmin && (
                                    <Button size="sm" variant="ghost" className="h-5 text-[10px] text-purple-600 mt-0.5 px-1" onClick={(e) => { e.stopPropagation(); openSubDialog(day, p, entry); }} data-testid={`assign-sub-${day}-${p}`}>
                                      <UserPlus className="w-3 h-3 mr-0.5" /> Substitute
                                    </Button>
                                  )}
                                </div>
                              ) : (
                                <div>
                                  {entry.teacherName && <p className="text-[10px] text-muted-foreground truncate">{entry.teacherName}</p>}
                                  {entry.startTime && <p className="text-[10px] text-muted-foreground">{entry.startTime}-{entry.endTime}</p>}
                                  {isAdmin && <button onClick={(e) => { e.stopPropagation(); deleteEntry(day, p); }} className="opacity-0 group-hover:opacity-100 absolute top-0 right-0 p-0.5"><Trash2 className="w-3 h-3 text-red-400" /></button>}
                                </div>
                              )}
                            </div>
                          ) : (
                            <div className={`p-1.5 rounded text-center ${isAdmin ? 'cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800' : ''}`} onClick={() => isAdmin && startEdit(day, p)} data-testid={`cell-${day}-${p}`}>
                              {isAdmin && <span className="text-[10px] text-muted-foreground/50">+ Add</span>}
                            </div>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* Legend */}
          <div className="flex gap-4 mt-3 text-xs text-muted-foreground border-t dark:border-slate-700 pt-3">
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-red-50 border border-red-200" /> Teacher on leave (needs sub)</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-purple-50 border border-purple-200" /> Substitute assigned</span>
          </div>
        </DataCard>
      )}

      {/* Monthly View */}
      {view === 'month' && (
        <DataCard>
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold flex items-center gap-2"><Calendar className="w-4 h-4" /> {MONTHS[currentDate.getMonth()]} {currentDate.getFullYear()}</h3>
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="icon" onClick={() => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1))}><ChevronLeft className="w-4 h-4" /></Button>
              <Button variant="ghost" size="icon" onClick={() => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1))}><ChevronRight className="w-4 h-4" /></Button>
            </div>
          </div>
          <div className="grid grid-cols-7 gap-px bg-slate-200 dark:bg-slate-700 rounded-lg overflow-hidden">
            {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(d => (
              <div key={d} className="p-2 text-center text-xs font-semibold text-muted-foreground bg-slate-50 dark:bg-slate-800">{d}</div>
            ))}
            {getMonthDays().map((day, i) => {
              const dateStr = day ? `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}` : null;
              const isToday = dateStr === today;
              const dayName = day ? new Date(currentDate.getFullYear(), currentDate.getMonth(), day).toLocaleDateString('en-US', { weekday: 'long' }) : '';
              const dayEntries = day ? timetable.filter(t => t.dayOfWeek === dayName) : [];
              const daySubs = day ? substitutes.filter(s => s.date === dateStr && s.classSection === selectedClass) : [];
              const dayLeaves = day && isAdmin ? getLeavesForDate(dateStr) : [];
              const isSunday = day ? new Date(currentDate.getFullYear(), currentDate.getMonth(), day).getDay() === 0 : false;
              return (
                <div key={i} className={`min-h-[80px] p-1.5 bg-white dark:bg-slate-900 ${isToday ? 'ring-2 ring-brand-deep-blue ring-inset' : ''} ${isSunday ? 'bg-red-50/50 dark:bg-red-900/10' : ''}`}>
                  {day && (
                    <>
                      <span className={`text-xs font-bold ${isToday ? 'text-brand-deep-blue' : ''}`}>{day}</span>
                      {isSunday ? <p className="text-[9px] text-red-400 mt-1">Holiday</p> : (
                        <div className="mt-1 space-y-0.5">
                          {dayLeaves.length > 0 && (
                            <div className="text-[9px] bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded px-1 truncate" title={dayLeaves.map(l => l.teacherName).join(', ')} data-testid={`leave-indicator-${dateStr}`}>
                              <AlertTriangle className="w-2.5 h-2.5 inline mr-0.5 -mt-px" />{dayLeaves.length} on leave
                            </div>
                          )}
                          {dayEntries.slice(0, 2).map((e, idx) => (
                            <div key={idx} className="text-[9px] bg-brand-deep-blue/10 text-brand-deep-blue rounded px-1 truncate">P{e.periodNumber}: {e.subject}</div>
                          ))}
                          {daySubs.length > 0 && <div className="text-[9px] bg-purple-100 text-purple-700 rounded px-1">{daySubs.length} sub(s)</div>}
                          {dayEntries.length > 2 && <p className="text-[9px] text-muted-foreground">+{dayEntries.length - 2} more</p>}
                        </div>
                      )}
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </DataCard>
      )}

      {/* Report View */}
      {view === 'report' && isAdmin && (
        <div className="space-y-5" data-testid="substitute-report">
          {loadingReport ? (
            <div className="flex items-center justify-center py-20"><div className="w-10 h-10 border-4 border-purple-400 border-t-transparent rounded-full animate-spin" /></div>
          ) : !report || report.totalSubstitutions === 0 ? (
            <DataCard className="text-center py-12">
              <BarChart3 className="w-10 h-10 mx-auto mb-3 text-muted-foreground/40" />
              <p className="text-sm text-muted-foreground">No substitute data yet.</p>
              <p className="text-xs text-muted-foreground mt-1">Assign substitutes from the Weekly view to see analytics here.</p>
            </DataCard>
          ) : (
            <>
              {/* Report Summary Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <DataCard className="text-center">
                  <div className="text-2xl font-bold text-purple-600" data-testid="report-total">{report.totalSubstitutions}</div>
                  <p className="text-xs text-muted-foreground mt-1">Total Substitutions</p>
                </DataCard>
                <DataCard className="text-center">
                  <div className="text-2xl font-bold text-red-500">{report.mostAbsentTeachers.length}</div>
                  <p className="text-xs text-muted-foreground mt-1">Teachers Needing Subs</p>
                </DataCard>
                <DataCard className="text-center">
                  <div className="text-2xl font-bold text-emerald-600">{report.mostActiveSubstitutes.length}</div>
                  <p className="text-xs text-muted-foreground mt-1">Active Substitutes</p>
                </DataCard>
                <DataCard className="text-center">
                  <div className="text-2xl font-bold text-brand-deep-blue">{report.classCoverage.length}</div>
                  <p className="text-xs text-muted-foreground mt-1">Classes Covered</p>
                </DataCard>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                {/* Most Absent Teachers */}
                <DataCard>
                  <h3 className="font-semibold text-sm flex items-center gap-2 mb-3"><UserMinus className="w-4 h-4 text-red-500" /> Most Frequently Absent</h3>
                  {report.mostAbsentTeachers.length === 0 ? (
                    <p className="text-xs text-muted-foreground text-center py-4">No data</p>
                  ) : (
                    <div className="space-y-2">
                      {report.mostAbsentTeachers.map((t, i) => (
                        <div key={t.teacherId} className="flex items-center justify-between p-2 rounded-lg bg-slate-50 dark:bg-slate-800/50" data-testid={`absent-teacher-${i}`}>
                          <div className="flex items-center gap-2">
                            <span className="w-6 h-6 rounded-full bg-red-100 dark:bg-red-900/30 text-red-600 text-xs font-bold flex items-center justify-center">{i + 1}</span>
                            <div>
                              <p className="text-sm font-medium">{t.name}</p>
                              {t.subject && <p className="text-[10px] text-muted-foreground">{t.subject}</p>}
                            </div>
                          </div>
                          <Badge className="bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400">{t.count} sub{t.count > 1 ? 's' : ''}</Badge>
                        </div>
                      ))}
                    </div>
                  )}
                </DataCard>

                {/* Most Active Substitutes */}
                <DataCard>
                  <h3 className="font-semibold text-sm flex items-center gap-2 mb-3"><TrendingUp className="w-4 h-4 text-emerald-500" /> Top Substitute Teachers</h3>
                  {report.mostActiveSubstitutes.length === 0 ? (
                    <p className="text-xs text-muted-foreground text-center py-4">No data</p>
                  ) : (
                    <div className="space-y-2">
                      {report.mostActiveSubstitutes.map((t, i) => (
                        <div key={t.teacherId} className="flex items-center justify-between p-2 rounded-lg bg-slate-50 dark:bg-slate-800/50" data-testid={`active-sub-${i}`}>
                          <div className="flex items-center gap-2">
                            <span className="w-6 h-6 rounded-full bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 text-xs font-bold flex items-center justify-center">{i + 1}</span>
                            <div>
                              <p className="text-sm font-medium">{t.name}</p>
                              {t.subject && <p className="text-[10px] text-muted-foreground">{t.subject}</p>}
                            </div>
                          </div>
                          <Badge className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">{t.count} cover{t.count > 1 ? 's' : ''}</Badge>
                        </div>
                      ))}
                    </div>
                  )}
                </DataCard>
              </div>

              {/* Charts Row */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                {/* Monthly Trend */}
                {report.monthlyTrend.length > 0 && (
                  <DataCard>
                    <h3 className="font-semibold text-sm flex items-center gap-2 mb-4"><BarChart3 className="w-4 h-4 text-brand-deep-blue" /> Monthly Trend</h3>
                    <ResponsiveContainer width="100%" height={220}>
                      <BarChart data={report.monthlyTrend}>
                        <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                        <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                        <RechartsTooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                        <Bar dataKey="count" name="Substitutions" fill="hsl(262, 80%, 55%)" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </DataCard>
                )}

                {/* Class Coverage Pie */}
                {report.classCoverage.length > 0 && (
                  <DataCard>
                    <h3 className="font-semibold text-sm flex items-center gap-2 mb-4"><Users className="w-4 h-4 text-purple-600" /> Substitutions by Class</h3>
                    <ResponsiveContainer width="100%" height={220}>
                      <PieChart>
                        <Pie data={report.classCoverage} dataKey="count" nameKey="classSection" cx="50%" cy="50%" outerRadius={80} label={({ classSection, count }) => `${classSection} (${count})`} labelLine={false}>
                          {report.classCoverage.map((_, idx) => (
                            <Cell key={idx} fill={['#7c3aed','#2563eb','#059669','#d97706','#dc2626','#8b5cf6','#0891b2','#65a30d'][idx % 8]} />
                          ))}
                        </Pie>
                        <RechartsTooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                      </PieChart>
                    </ResponsiveContainer>
                  </DataCard>
                )}
              </div>

              {/* Refresh button */}
              <div className="flex justify-end">
                <Button variant="outline" size="sm" onClick={loadReport} data-testid="refresh-report-btn"><RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh Report</Button>
              </div>
            </>
          )}
        </div>
      )}

      {/* Substitute Assignment Dialog */}
      <Dialog open={!!subDialog} onOpenChange={() => setSubDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><UserPlus className="w-5 h-5 text-purple-600" /> Assign Substitute</DialogTitle>
            <DialogDescription>
              {subDialog && <>Replace <strong>{subDialog.entry?.teacherName}</strong> for <strong>{subDialog.entry?.subject}</strong> — {selectedClass}, Period {subDialog.period}, {subDialog.day} ({subDialog.date})</>}
            </DialogDescription>
          </DialogHeader>
          <div className="mt-2">
            {loadingSubs ? (
              <div className="flex items-center justify-center py-8"><div className="w-8 h-8 border-4 border-purple-400 border-t-transparent rounded-full animate-spin" /></div>
            ) : availableSubs.length === 0 ? (
              <div className="text-center py-6 text-muted-foreground">
                <Users className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No available teachers for this slot.</p>
                <p className="text-xs mt-1">All teachers are either on leave, teaching, or already substituting.</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {availableSubs.map(t => (
                  <div key={t.teacherId} className="flex items-center justify-between p-3 border border-slate-200 dark:border-slate-700 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors" data-testid={`sub-option-${t.teacherId}`}>
                    <div>
                      <p className="text-sm font-medium">{t.name}</p>
                      <p className="text-xs text-muted-foreground">{t.subject || 'General'} {t.assignedClasses?.length > 0 ? `• ${t.assignedClasses.join(', ')}` : ''}</p>
                    </div>
                    <Button size="sm" className="bg-purple-600 hover:bg-purple-700 text-white" onClick={() => assignSub(t.teacherId)} disabled={assigningSub} data-testid={`assign-${t.teacherId}`}>
                      <UserPlus className="w-3 h-3 mr-1" /> Assign
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setSubDialog(null)}>Close</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Config Dialog */}
      <Dialog open={showConfig} onOpenChange={setShowConfig}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Timetable Configuration</DialogTitle>
            <DialogDescription>Set the number of periods and timing.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div><Label>Periods Per Day</Label><Input type="number" min={1} max={12} value={configForm.periodsPerDay} onChange={e => setConfigForm({...configForm, periodsPerDay: parseInt(e.target.value) || 8})} data-testid="config-periods" /></div>
            <div><Label>Period Duration (minutes)</Label><Input type="number" min={20} max={90} value={configForm.periodDuration} onChange={e => setConfigForm({...configForm, periodDuration: parseInt(e.target.value) || 45})} data-testid="config-duration" /></div>
            <div><Label>Start Time</Label><Input type="time" value={configForm.startTime} onChange={e => setConfigForm({...configForm, startTime: e.target.value})} data-testid="config-start-time" /></div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowConfig(false)}>Cancel</Button>
            <Button onClick={saveConfig} className="bg-brand-deep-blue hover:bg-brand-deep-blue/90" data-testid="save-config-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
